import { useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SiteShellHeader } from "@/components/SiteShellHeader";
import type { SiteMode } from "@/components/siteModes";
import { areaOf, isPlayback, ME_PATH } from "@/routes/paths";
import { HeaderActionsContext } from "@/routes/headerActions";
import { useCurrentSiteId } from "@/hooks/useCurrentSiteId";

/**
 * 헤더가 이 층에 산다.
 *
 * 예전에는 일곱 페이지가 각자 `<SiteShellHeader>` 를 렌더했다. 라우트가 바뀔 때마다
 * 헤더·영역 전환기·현장 드롭다운이 통째로 언마운트-리마운트되어, 영역을 오갈 때
 * 깜빡이고 드롭다운이 매번 닫혔다.
 *
 * 페이지가 헤더에 넘기던 것들은 여기서 주소로 구한다 — 현장 id 는 `useCurrentSiteId`,
 * 현장 이름은 드롭다운이 자기 목록에서 찾는다. 페이지가 알려줄 것이 남아 있지 않다.
 */
const HEADER_TITLES: Record<string, string> = {
  [ME_PATH]: "내 정보",
};

function modeOf(pathname: string): SiteMode {
  const area = areaOf(pathname);
  if (area === "cctv") return isPlayback(pathname) ? "playback" : "cctv";
  if (area === "health") return "health";
  return "site";
}

export function AppShell() {
  const { pathname } = useLocation();
  const siteId = useCurrentSiteId();
  const cctv = areaOf(pathname) === "cctv";
  const [actions, setActions] = useState<ReactNode | null>(null);
  const [navigationActions, setNavigationActions] = useState<ReactNode | null>(null);

  return (
    <HeaderActionsContext value={{ setActions, setNavigationActions }}>
      <div className="flex h-full min-h-0 w-full flex-col">
        <SiteShellHeader
          current={modeOf(pathname)}
          siteId={siteId ?? undefined}
          back={cctv ? false : undefined}
          switcher={cctv ? false : undefined}
          title={HEADER_TITLES[pathname]}
          variant="light"
          right={actions}
          navigationActions={navigationActions}
        />
        <div className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </div>
      </div>
    </HeaderActionsContext>
  );
}
